import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { trafficLight, fmtEUR, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Check, FileText, Building2, User, CheckCircle2, MessageSquarePlus, FileDown, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import ShareDialog from "../components/ShareDialog";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [client, setClient] = useState(null);
  const [openMs, setOpenMs] = useState(false);
  const [openInv, setOpenInv] = useState(false);
  const [ms, setMs] = useState({ name: "", percentage: 20, due_date: "", completed: false });
  const [inv, setInv] = useState({ amount: 0, description: "", status: "accrued" });
  const [noteText, setNoteText] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    const r = await api.get(`/projects/${id}`);
    setP(r.data);
    const c = await api.get(`/clients/${r.data.client_id}`);
    setClient(c.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!p) return <div className="p-10">Caricamento...</div>;
  const tl = trafficLight(p.deadline, p.delivered);

  const toggleDelivered = async () => {
    await api.put(`/projects/${id}`, { delivered: !p.delivered });
    toast.success(p.delivered ? "Marcata come in corso" : "Marcata come consegnata");
    load();
  };

  const patchProject = async (patch) => {
    await api.put(`/projects/${id}`, patch);
    load();
  };

  const addMs = async (e) => {
    e.preventDefault();
    await api.post(`/projects/${id}/milestones`, { ...ms, percentage: Number(ms.percentage), due_date: ms.due_date || null });
    setOpenMs(false);
    setMs({ name: "", percentage: 20, due_date: "", completed: false });
    toast.success("Milestone aggiunta");
    load();
  };

  const toggleMs = async (m) => {
    await api.put(`/projects/${id}/milestones/${m.id}`, { ...m, completed: !m.completed });
    load();
  };

  const delMs = async (mid) => {
    await api.delete(`/projects/${id}/milestones/${mid}`);
    load();
  };

  const addInv = async (e) => {
    e.preventDefault();
    await api.post(`/projects/${id}/invoices`, { ...inv, amount: Number(inv.amount) });
    setOpenInv(false); setInv({ amount: 0, description: "", status: "accrued" });
    toast.success("Voce aggiunta");
    load();
  };

  const setInvStatus = async (i, status) => {
    await api.put(`/projects/${id}/invoices/${i.id}`, { ...i, status });
    load();
  };

  const delInv = async (iid) => {
    await api.delete(`/projects/${id}/invoices/${iid}`);
    load();
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    await api.post(`/projects/${id}/notes`, { text: noteText.trim() });
    setNoteText("");
    toast.success("Nota aggiunta");
    load();
  };

  const delNote = async (nid) => {
    await api.delete(`/projects/${id}/notes/${nid}`);
    load();
  };

  const exportPdf = async () => {
    try {
      const t = localStorage.getItem("crm_token");
      const r = await fetch(`${API}/projects/${id}/export.pdf`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error("err");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `${p.title.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch { toast.error("Errore export PDF"); }
  };

  const duplicate = async () => {
    if (!window.confirm("Duplicare questa gara? Milestone verranno copiate (senza date). Fatture e note non vengono copiate.")) return;
    try {
      const r = await api.post(`/projects/${id}/duplicate`);
      toast.success("Gara duplicata");
      nav(`/projects/${r.data.id}`);
    } catch (e) { toast.error("Errore duplicazione"); }
  };

  const statusColors = {
    accrued: "bg-neutral-100 text-neutral-700 border-neutral-300",
    invoiced: "bg-blue-50 text-blue-700 border-blue-200",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };

  return (
    <div className="p-8 lg:p-10 max-w-6xl space-y-6 fade-up">
      <button onClick={() => nav(-1)} className="mono-label flex items-center gap-1.5" data-testid="back-btn">
        <ArrowLeft className="h-3 w-3" /> Indietro
      </button>

      <header className="flex items-start justify-between gap-4 border-b border-neutral-200 pb-6">
        <div className="min-w-0">
          <div className="mono-label flex items-center gap-1.5">
            {p.type === "pa" ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {p.type === "pa" ? "Pubblica Amministrazione" : "Cliente Privato"} · {client?.name}
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-medium mt-2 leading-tight">{p.title}</h1>
          {p.description && <p className="text-sm text-neutral-600 mt-2 max-w-2xl">{p.description}</p>}
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1.5 border font-mono text-xs font-semibold ${tl.className}`} data-testid="traffic-light-badge">
            {tl.level === "green" ? "CONSEGNATA" : `SCADENZA · ${tl.label}`}
          </div>
          <Button onClick={exportPdf} data-testid="export-pdf-btn" variant="outline">
            <FileDown className="h-4 w-4 mr-1.5" /> PDF
          </Button>
          <Button onClick={duplicate} data-testid="duplicate-btn" variant="outline">
            <Copy className="h-4 w-4 mr-1.5" /> Duplica
          </Button>
          <Button onClick={() => setShareOpen(true)} data-testid="share-btn" variant="outline">
            <Share2 className="h-4 w-4 mr-1.5" /> Condividi
          </Button>
          <Button onClick={toggleDelivered} data-testid="toggle-delivered" variant={p.delivered ? "outline" : "default"} className={p.delivered ? "" : "brand-bg hover:opacity-90"}>
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {p.delivered ? "Riapri" : "Segna come consegnata"}
          </Button>
        </div>
      </header>
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} projectId={id} />

      {/* Key numbers grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-0 border border-neutral-200 bg-white">
        {[
          { l: "Importo lavori", v: fmtEUR(p.importo_lavori) },
          { l: "Aliquota", v: `${p.fee_percentage || 6.5}%` },
          { l: "Parcella", v: fmtEUR(p.parcella_calculated) },
          { l: "Scadenza", v: fmtDate(p.deadline) },
          { l: "CIG · CUP", v: `${p.cig || "—"} · ${p.cup || "—"}` },
        ].map((s, i) => (
          <div key={s.l} className={`p-4 ${i > 0 ? "border-l border-neutral-200" : ""}`}>
            <div className="mono-label">{s.l}</div>
            <div className="font-mono text-base font-semibold mt-1 text-neutral-900 truncate">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Financial totals */}
      <div className="grid grid-cols-3 gap-0 border border-neutral-200 bg-white">
        {[
          { l: "Maturato", v: p.total_accrued, cls: "text-neutral-700" },
          { l: "Fatturato", v: p.total_invoiced, cls: "text-blue-700" },
          { l: "Pagato", v: p.total_paid, cls: "text-emerald-700" },
        ].map((s, i) => (
          <div key={s.l} className={`p-5 ${i > 0 ? "border-l border-neutral-200" : ""}`} data-testid={`total-${s.l.toLowerCase()}`}>
            <div className="mono-label">{s.l}</div>
            <div className={`font-mono text-2xl font-semibold mt-1 ${s.cls}`}>{fmtEUR(s.v)}</div>
          </div>
        ))}
      </div>

      {/* Milestones */}
      <div className="border border-neutral-200 bg-white">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div><div className="mono-label">SAL / Milestone</div><h2 className="font-display text-xl font-medium mt-1">Stati Avanzamento</h2></div>
          <Dialog open={openMs} onOpenChange={setOpenMs}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-milestone-btn" variant="outline"><Plus className="h-3.5 w-3.5 mr-1" /> Milestone</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Nuova milestone</DialogTitle></DialogHeader>
              <form onSubmit={addMs} className="space-y-3">
                <div><Label>Nome *</Label><Input required value={ms.name} onChange={(e) => setMs({ ...ms, name: e.target.value })} data-testid="input-ms-name" /></div>
                <div><Label>% Parcella maturata</Label><Input type="number" step="0.1" value={ms.percentage} onChange={(e) => setMs({ ...ms, percentage: e.target.value })} data-testid="input-ms-pct" /></div>
                <div><Label>Scadenza (opz.)</Label><Input type="date" value={ms.due_date} onChange={(e) => setMs({ ...ms, due_date: e.target.value })} /></div>
                <Button type="submit" data-testid="submit-milestone" className="w-full brand-bg hover:opacity-90">Aggiungi</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="divide-y divide-neutral-200">
          {(p.milestones || []).length === 0 && <div className="p-8 text-center text-sm text-neutral-500">Nessuna milestone</div>}
          {(p.milestones || []).map((m) => (
            <div key={m.id} className="p-4 flex items-center gap-4" data-testid={`milestone-${m.id}`}>
              <button onClick={() => toggleMs(m)} className={`h-5 w-5 border-2 flex items-center justify-center ${m.completed ? "brand-bg brand-border" : "border-neutral-300"}`}>
                {m.completed && <Check className="h-3 w-3 text-white" />}
              </button>
              <div className="flex-1">
                <div className={`text-sm font-medium ${m.completed ? "text-neutral-500 line-through" : "text-neutral-900"}`}>{m.name}</div>
                <div className="mono-label mt-0.5">{m.percentage}% · {fmtEUR(p.parcella_calculated * m.percentage / 100)}{m.due_date && ` · ${fmtDate(m.due_date)}`}</div>
              </div>
              <button onClick={() => delMs(m.id)} className="text-neutral-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Invoices */}
      <div className="border border-neutral-200 bg-white">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div><div className="mono-label">Fatturazione</div><h2 className="font-display text-xl font-medium mt-1">Voci economiche</h2></div>
          <Dialog open={openInv} onOpenChange={setOpenInv}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-invoice-btn" variant="outline"><Plus className="h-3.5 w-3.5 mr-1" /> Voce</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Nuova voce</DialogTitle></DialogHeader>
              <form onSubmit={addInv} className="space-y-3">
                <div><Label>Importo €</Label><Input type="number" step="0.01" required value={inv.amount} onChange={(e) => setInv({ ...inv, amount: e.target.value })} data-testid="input-inv-amount" /></div>
                <div><Label>Descrizione</Label><Input value={inv.description} onChange={(e) => setInv({ ...inv, description: e.target.value })} /></div>
                <div><Label>Stato</Label>
                  <select value={inv.status} onChange={(e) => setInv({ ...inv, status: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    <option value="accrued">Maturato</option>
                    <option value="invoiced">Fatturato</option>
                    <option value="paid">Pagato</option>
                  </select>
                </div>
                <Button type="submit" data-testid="submit-invoice" className="w-full brand-bg hover:opacity-90">Aggiungi</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="divide-y divide-neutral-200">
          {(p.invoices || []).length === 0 && <div className="p-8 text-center text-sm text-neutral-500">Nessuna voce economica</div>}
          {(p.invoices || []).map((i) => (
            <div key={i.id} className="p-4 flex items-center gap-4" data-testid={`invoice-${i.id}`}>
              <FileText className="h-4 w-4 text-neutral-400" />
              <div className="flex-1">
                <div className="font-mono font-semibold">{fmtEUR(i.amount)}</div>
                <div className="text-xs text-neutral-500">{i.description || "—"}</div>
              </div>
              <div className="flex gap-1">
                {["accrued", "invoiced", "paid"].map((s) => (
                  <button key={s} onClick={() => setInvStatus(i, s)} data-testid={`inv-status-${i.id}-${s}`}
                    className={`px-2.5 py-1 text-xs border font-mono uppercase tracking-wider ${i.status === s ? statusColors[s] : "text-neutral-400 border-neutral-200"}`}>
                    {s === "accrued" ? "MAT" : s === "invoiced" ? "FAT" : "PAG"}
                  </button>
                ))}
              </div>
              <button onClick={() => delInv(i.id)} className="text-neutral-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Notes / Activity log */}
      <div className="border border-neutral-200 bg-white">
        <div className="p-5 border-b border-neutral-200">
          <div className="mono-label">Diario</div>
          <h2 className="font-display text-xl font-medium mt-1">Note & Attività</h2>
        </div>
        <form onSubmit={addNote} className="p-4 border-b border-neutral-200 flex gap-2">
          <input
            type="text"
            data-testid="note-input"
            placeholder="Aggiungi una nota, chiamata, prossimo passo..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            className="flex-1 border border-neutral-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-neutral-500"
          />
          <Button type="submit" size="sm" data-testid="add-note-btn" className="brand-bg hover:opacity-90">
            <MessageSquarePlus className="h-4 w-4 mr-1" /> Aggiungi
          </Button>
        </form>
        <div className="divide-y divide-neutral-100">
          {(p.notes || []).length === 0 && <div className="p-8 text-center text-sm text-neutral-500">Nessuna nota. Traccia chiamate, prossimi passi, promemoria.</div>}
          {[...(p.notes || [])].reverse().map((n) => (
            <div key={n.id} className="p-4 flex gap-3 group" data-testid={`note-${n.id}`}>
              <div className="mt-1.5 h-1.5 w-1.5 brand-bg rounded-full flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm text-neutral-800 whitespace-pre-wrap">{n.text}</div>
                <div className="mono-label mt-1">{fmtDate(n.created_at)}</div>
              </div>
              <button onClick={() => delNote(n.id)} className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600 transition"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
